/**
 * ATS accounts.
 *
 * The password comes back on create and on reset and nowhere else. Nothing here caches
 * it, and there is no query that could: the server does not keep it either.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type { AtsCandidate, AtsReferral, IssuedAccount } from '@/api/schema';

export const ATS_KEY = ['ats'] as const;

export function useAtsCandidates(search: string) {
  return useQuery({
    queryKey: [...ATS_KEY, 'candidates', search],
    queryFn: () =>
      api.get<AtsCandidate[]>('/api/v1/ats/candidates', {
        query: search ? { search } : {},
      }),
    placeholderData: (previous) => previous,
  });
}

export function useIssueAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (candidateId: string) =>
      api.post<IssuedAccount>(`/api/v1/ats/candidates/${candidateId}/account`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ATS_KEY });
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useResetAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (candidateId: string) =>
      api.post<IssuedAccount>(`/api/v1/ats/candidates/${candidateId}/account/reset`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ATS_KEY });
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// --- referrals ------------------------------------------------------------- //

export function useReferrals(candidateId: string | null) {
  return useQuery({
    queryKey: [...ATS_KEY, 'referrals', candidateId],
    queryFn: () =>
      api.get<AtsReferral[]>(`/api/v1/ats/candidates/${candidateId ?? ''}/referrals`),
    enabled: candidateId !== null,
  });
}

function useReferralMutation<T>(fn: (input: T) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      // The list too: the ratio chip is computed there.
      await queryClient.invalidateQueries({ queryKey: ATS_KEY });
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useAddReferral(candidateId: string) {
  return useReferralMutation(({ jobTitle, country }: { jobTitle: string; country: string }) =>
    api.post<AtsReferral>(`/api/v1/ats/candidates/${candidateId}/referrals`, {
      job_title: jobTitle,
      country,
    }),
  );
}

export function useShareReferral() {
  return useReferralMutation(({ id, shared }: { id: string; shared: boolean }) =>
    api.patch<AtsReferral>(`/api/v1/ats/referrals/${id}`, { shared }),
  );
}

export function useRemoveReferral() {
  return useReferralMutation((id: string) => api.delete<never>(`/api/v1/ats/referrals/${id}`));
}
